import { useEffect, useMemo, useState } from "react";
import { formatDate, formatDateShort } from "../../lib/format";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  tmApi,
  type TmActivityView,
  type TmMeetingView,
  type TmProjectView,
  type TmTaskView,
} from "../../api/tm";
import { TmStatusBadge } from "../../components/tm/TmStatusBadge";
import { TmPriorityPill } from "../../components/tm/TmPriorityPill";
import { useAuth } from "../../auth/AuthContext";

/**
 * TM landing — single screen that summarises everything across Tasks /
 * Projects / Meetings / Activity for the logged-in user. Designed for "first
 * thing in the morning, what needs attention from me today" usage.
 */
export default function TmOverviewPage() {
  const { t } = useTranslation();
  const { state } = useAuth();

  const [myTasks, setMyTasks] = useState<TmTaskView[] | null>(null);
  const [forValidation, setForValidation] = useState<TmTaskView[] | null>(null);
  const [projects, setProjects] = useState<TmProjectView[] | null>(null);
  const [meetings, setMeetings] = useState<TmMeetingView[] | null>(null);
  const [activity, setActivity] = useState<TmActivityView[] | null>(null);

  useEffect(() => {
    void tmApi.mine().then(setMyTasks).catch(() => setMyTasks([]));
    void tmApi.forValidation().then(setForValidation).catch(() => setForValidation([]));
    void tmApi.listProjects().then(setProjects).catch(() => setProjects([]));
    void tmApi.listMeetings().then(setMeetings).catch(() => setMeetings([]));
    void tmApi.listActivity({ size: 10 }).then((p) => setActivity(p.content)).catch(() => setActivity([]));
  }, []);

  const today = useMemo(() => new Date(new Date().toDateString()), []);
  const oneWeek = useMemo(() => {
    const d = new Date(today); d.setDate(d.getDate() + 7); return d;
  }, [today]);

  const taskStats = useMemo(() => {
    const t = myTasks ?? [];
    const open = t.filter((x) => x.status !== "DONE" && x.status !== "CANCELLED").length;
    const overdue = t.filter((x) =>
      x.dueDate
      && new Date(x.dueDate) < today
      && x.status !== "DONE"
      && x.status !== "CANCELLED").length;
    const dueWeek = t.filter((x) =>
      x.dueDate
      && new Date(x.dueDate) >= today
      && new Date(x.dueDate) <= oneWeek
      && x.status !== "DONE"
      && x.status !== "CANCELLED").length;
    const blocked = t.filter((x) => x.status === "BLOCKED").length;
    return { open, overdue, dueWeek, blocked };
  }, [myTasks, today, oneWeek]);

  const projectStats = useMemo(() => {
    const p = projects ?? [];
    return {
      active:    p.filter((x) => x.status === "ACTIVE").length,
      planning:  p.filter((x) => x.status === "PLANNING").length,
      onHold:    p.filter((x) => x.status === "ON_HOLD").length,
      total:     p.length,
    };
  }, [projects]);

  const meetingStats = useMemo(() => {
    const ms = meetings ?? [];
    const now = Date.now();
    const todayStart = today.getTime();
    const todayEnd = todayStart + 86_400_000;
    const todayCount = ms.filter((m) => {
      const t = new Date(m.scheduledAt).getTime();
      return t >= todayStart && t < todayEnd;
    }).length;
    const upcoming = ms.filter((m) => new Date(m.scheduledAt).getTime() >= now).length;
    return { todayCount, upcoming };
  }, [meetings, today]);

  const needsAction = useMemo(() => {
    const own = (myTasks ?? []).filter((x) =>
      x.status !== "DONE" && x.status !== "CANCELLED");
    const validate = forValidation ?? [];
    // Sort: overdue first, then by due date ascending, validators after my own
    return [
      ...own.sort((a, b) => sortByDue(a, b, today)),
      ...validate.map((x) => ({ ...x, _validating: true })),
    ].slice(0, 8) as (TmTaskView & { _validating?: boolean })[];
  }, [myTasks, forValidation, today]);

  const upcomingMeetings = useMemo(() => {
    const now = Date.now();
    return (meetings ?? [])
      .filter((m) => new Date(m.scheduledAt).getTime() >= now - 30 * 60_000)
      .sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime())
      .slice(0, 5);
  }, [meetings]);

  const activeProjects = useMemo(() =>
    (projects ?? []).filter((p) => p.status === "ACTIVE" || p.status === "PLANNING").slice(0, 6),
    [projects]);

  const hour = new Date().getHours();
  const greeting = hour < 12
    ? t("tm.overview.greeting.morning", "Good morning")
    : hour < 18
      ? t("tm.overview.greeting.afternoon", "Good afternoon")
      : t("tm.overview.greeting.evening", "Good evening");
  const firstName = state.status === "authenticated" ? state.user.firstName : "";

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="page-head">
        <h1 style={{ margin: 0, fontSize: 24, color: "var(--ink)" }}>{greeting}{firstName ? `, ${firstName}` : ""}</h1>
        <div className="sub">{t("tm.overview.subtitle", "Your Task Manager at a glance")}</div>
      </div>

      <div style={kpiGrid}>
        <KpiTile label={t("tm.overview.kpi.openTasks", "Open tasks")}    value={taskStats.open}    accent="var(--info)" bg="var(--info-bg)" hint={t("tm.overview.kpi.openTasks.hint", "Yours — not done, not cancelled")} />
        <KpiTile label={t("tm.tasks.detail.overdue", "Overdue")}       value={taskStats.overdue} accent="var(--err)" bg="var(--err-bg)" hint={t("tm.overview.kpi.overdue.hint", "Due-date passed")} danger={taskStats.overdue > 0} />
        <KpiTile label={t("tm.overview.kpi.dueThisWeek", "Due this week")} value={taskStats.dueWeek} accent="var(--warn)" bg="var(--warn-bg)" hint={t("tm.overview.kpi.dueThisWeek.hint", "Next 7 days")} />
        <KpiTile label={t("tm.overview.kpi.blocked", "Blocked")}       value={taskStats.blocked} accent="var(--ink-3)" bg="var(--surface-muted)" hint={t("tm.overview.kpi.blocked.hint", "Waiting on an unblock")} />
        <KpiTile label={t("tm.overview.kpi.activeProjects", "Active proj.")}  value={projectStats.active} accent="var(--ok)" bg="var(--ok-bg)" hint={t("tm.overview.kpi.activeProjects.hint", { defaultValue: "{{total}} total · {{planning}} planning", total: projectStats.total, planning: projectStats.planning })} />
        <KpiTile label={t("tm.overview.kpi.meetingsToday", "Meetings today")} value={meetingStats.todayCount} accent="var(--tone-violet)" bg="var(--tone-violet-bg)" hint={t("tm.overview.kpi.meetingsToday.hint", { defaultValue: "{{count}} upcoming overall", count: meetingStats.upcoming })} />
      </div>

      <div style={twoCol}>
        <section style={card}>
          <header style={cardHead}>
            <h2 style={cardTitle}>{t("tm.overview.needsAction.title", "Needs your action")}</h2>
            <Link to="/tm/tasks" style={actionLink}>{t("tm.overview.needsAction.openTasksLink", "Open tasks →")}</Link>
          </header>
          {needsAction.length === 0 ? (
            <Muted>{t("tm.overview.needsAction.empty", "Inbox zero. Nothing waiting on you right now.")}</Muted>
          ) : (
            <ul style={list}>
              {needsAction.map((task) => {
                const overdue =
                  !!task.dueDate
                  && new Date(task.dueDate) < today
                  && task.status !== "DONE"
                  && task.status !== "CANCELLED";
                return (
                  <li key={task.id} style={{
                    ...listRow,
                    ...(overdue ? { boxShadow: "inset 3px 0 0 var(--err)" } : {}),
                  }}>
                    <Link to={`/tm/tasks/${task.id}`} style={{ flex: 1, color: "var(--ink)", textDecoration: "none", minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {task._validating && (
                          <span style={validateBadge}>{t("tm.overview.validateBadge", "VALIDATE")}</span>
                        )}
                        {task.title}
                      </div>
                      <div style={{ fontSize: 11.5, color: "var(--ink-3)", marginTop: 2 }}>
                        {task.assigneeName}
                        {task.projectName ? ` · ${task.projectName}` : ""}
                        {task.dueDate ? ` · ${overdue ? t("tm.overview.task.overduePrefix", "Overdue, due ") : t("tm.overview.task.duePrefix", "Due ")}${fmtDate(task.dueDate)}` : ""}
                      </div>
                    </Link>
                    <TmPriorityPill priority={task.priority} />
                    <TmStatusBadge status={task.status} />
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section style={card}>
          <header style={cardHead}>
            <h2 style={cardTitle}>{t("tm.overview.meetings.title", "Upcoming meetings")}</h2>
            <Link to="/tm/meetings" style={actionLink}>{t("tm.overview.meetings.allLink", "All meetings →")}</Link>
          </header>
          {upcomingMeetings.length === 0 ? (
            <Muted>{t("tm.overview.meetings.empty", "No meetings on the horizon.")}</Muted>
          ) : (
            <ul style={list}>
              {upcomingMeetings.map((m) => {
                const start = new Date(m.scheduledAt);
                const end = new Date(start.getTime() + (m.durationMinutes ?? 30) * 60_000);
                const live = start <= new Date() && new Date() < end;
                return (
                  <li key={m.id} style={{ ...listRow, gap: 12, alignItems: "center" }}>
                    <div style={timeBlock}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>
                        {start.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--ink-4)" }}>
                        {fmtRelDay(start, today, t)}
                      </div>
                    </div>
                    <Link to={`/tm/meetings/${m.id}`} style={{ flex: 1, color: "var(--ink)", textDecoration: "none", minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {m.title}
                        {live && <span style={liveBadge}>{t("tm.overview.liveBadge", "LIVE")}</span>}
                      </div>
                      <div style={{ fontSize: 11.5, color: "var(--ink-3)", marginTop: 2 }}>
                        {t("tm.overview.meetings.duration", { defaultValue: "{{count}} min", count: m.durationMinutes })}
                        {m.projectName ? ` · ${m.projectName}` : ""}
                        {m.location ? ` · ${m.location}` : ""}
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      <div style={twoCol}>
        <section style={card}>
          <header style={cardHead}>
            <h2 style={cardTitle}>{t("tm.overview.projects.title", "Active projects")}</h2>
            <Link to="/tm/projects" style={actionLink}>{t("tm.overview.projects.allLink", "All projects →")}</Link>
          </header>
          {activeProjects.length === 0 ? (
            <Muted>{t("tm.overview.projects.empty", "No active projects yet.")}</Muted>
          ) : (
            <ul style={projectGrid}>
              {activeProjects.map((p) => (
                <li key={p.id} style={{ listStyle: "none" }}>
                  <Link to={`/tm/projects/${p.id}`} style={{ textDecoration: "none", color: "inherit" }}>
                    <div style={projCard}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: "var(--ink)" }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 4 }}>
                        {p.ownerName}{p.teamName ? ` · ${p.teamName}` : ""}
                      </div>
                      <div style={{ marginTop: 6, ...projStatusPill(p.status) }}>{t(`tm.project.status.${p.status}`, p.status.replace("_", " "))}</div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section style={card}>
          <header style={cardHead}>
            <h2 style={cardTitle}>{t("tm.overview.activity.title", "Recent activity")}</h2>
            <Link to="/tm/activity" style={actionLink}>{t("tm.overview.activity.fullFeedLink", "Full feed →")}</Link>
          </header>
          {!activity || activity.length === 0 ? (
            <Muted>{t("tm.overview.activity.empty", "No activity yet.")}</Muted>
          ) : (
            <ul style={list}>
              {activity.slice(0, 8).map((evt) => (
                <li key={evt.id} style={{ ...listRow, gap: 10, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 16, marginTop: 2 }}>{activityIcon(evt.entityType)}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, color: "var(--ink-2)" }}>
                      <b style={{ color: "var(--ink)" }}>{evt.actorName ?? t("tm.overview.activity.system", "System")}</b>{" "}
                      <span style={{ color: "var(--ink-3)" }}>{evt.action.toLowerCase().replace(/_/g, " ")}</span>
                    </div>
                    <div style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 2 }}>{relTime(evt.createdAt, t)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function KpiTile({
  label, value, accent, bg, hint, danger,
}: {
  label: string; value: number; accent: string; bg: string; hint?: string; danger?: boolean;
}) {
  return (
    <div style={kpiCard}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</span>
        <span style={{ width: 8, height: 8, borderRadius: 999, background: accent, boxShadow: `0 0 0 4px ${bg}` }} />
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: danger && value > 0 ? "var(--err)" : "var(--ink)", marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
        {value}
      </div>
      {hint && <div style={{ fontSize: 11, color: "var(--ink-4)" }}>{hint}</div>}
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <div style={{ color: "var(--ink-3)", fontSize: 13 }}>{children}</div>;
}

function sortByDue(a: TmTaskView, b: TmTaskView, today: Date): number {
  const aD = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
  const bD = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
  const aOverdue = a.dueDate && new Date(a.dueDate) < today ? 1 : 0;
  const bOverdue = b.dueDate && new Date(b.dueDate) < today ? 1 : 0;
  if (aOverdue !== bOverdue) return bOverdue - aOverdue;
  return aD - bD;
}

function activityIcon(t: TmActivityView["entityType"]): string {
  switch (t) {
    case "TASK":    return "✅";
    case "PROJECT": return "📂";
    case "MEETING": return "📅";
    case "COMMENT": return "💬";
  }
}

function fmtDate(iso: string): string {
  return formatDateShort(iso);
}

function fmtRelDay(d: Date, today: Date, t: TFunction): string {
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  if (diff === 0) return t("tm.overview.relDay.today", "today");
  if (diff === 1) return t("tm.overview.relDay.tomorrow", "tomorrow");
  if (diff < 0) return t("tm.overview.relDay.daysAgo", { defaultValue: "{{count}}d ago", count: Math.abs(diff) });
  return formatDateShort(d);
}

function relTime(iso: string, t: TFunction): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return t("tm.overview.relTime.justNow", "just now");
  if (min < 60) return t("tm.overview.relTime.minutesAgo", { defaultValue: "{{count}}m ago", count: min });
  const h = Math.floor(min / 60);
  if (h < 24) return t("tm.overview.relTime.hoursAgo", { defaultValue: "{{count}}h ago", count: h });
  const d = Math.floor(h / 24);
  if (d < 7) return t("tm.overview.relTime.daysAgo", { defaultValue: "{{count}}d ago", count: d });
  return formatDate(iso);
}

function projStatusPill(s: TmProjectView["status"]): React.CSSProperties {
  const palette: Record<typeof s, { bg: string; fg: string }> = {
    ACTIVE:    { bg: "var(--ok-bg)", fg: "var(--ok)" },
    PLANNING:  { bg: "var(--warn-bg)", fg: "var(--warn)" },
    ON_HOLD:   { bg: "var(--surface-muted)", fg: "var(--ink-3)" },
    COMPLETED: { bg: "var(--info-bg)", fg: "var(--info)" },
    CANCELLED: { bg: "var(--err-bg)", fg: "var(--err)" },
  };
  const p = palette[s];
  return {
    background: p.bg, color: p.fg,
    padding: "1px 8px", borderRadius: 999, display: "inline-block",
    fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4,
  };
}

const kpiGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 };
const kpiCard: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: "12px 14px" };
const twoCol: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 14 };
const card: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: 16, display: "flex", flexDirection: "column", gap: 12 };
const cardHead: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center" };
const cardTitle: React.CSSProperties = { margin: 0, fontSize: 14, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700 };
const list: React.CSSProperties = { listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 };
const listRow: React.CSSProperties = { display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 8 };
const timeBlock: React.CSSProperties = { minWidth: 60, padding: "4px 8px", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 6, textAlign: "center" };
const projectGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8, padding: 0, margin: 0 };
const projCard: React.CSSProperties = { background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 8, padding: 10, transition: "transform .12s ease" };
const actionLink: React.CSSProperties = { fontSize: 12, color: "var(--brand-primary)", textDecoration: "none", fontWeight: 600 };
const liveBadge: React.CSSProperties = { marginLeft: 8, fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 999, background: "var(--ok-bg)", color: "var(--ok)", letterSpacing: 0.5, textTransform: "uppercase" };
const validateBadge: React.CSSProperties = { marginRight: 6, fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: "var(--warn-bg)", color: "var(--warn)", letterSpacing: 0.5 };
